/**
 * Every route in the app, in one place.
 *
 * An `as const` object rather than an enum, because `device()` needs to build a
 * path from a serial number and a TS string enum cannot express that.
 *
 * `App.tsx` imports this for its route table too, so the routes and every
 * `navigate()` call share one source of truth.
 */
export const paths = {
  home: "/",
  signInUp: "/sign-in-up",
  forgotPassword: "/forgot-password",

  guides: "/guides",

  register: "/register",
  registerSerial: "/register/serial",
  registerProjector: "/register/projector",
  registerOwner: "/register/owner",
  registerSummary: "/register/summary",

  account: "/account",
  accountDevices: "/account/devices",

  devicePattern: "/devices/:artomeSerialNo",
  device: (serial: string) => `/devices/${encodeURIComponent(serial)}`,
} as const;

/**
 * Redirects for routes that existed before the installation guides moved to
 * artome.fi. Installers have had these URLs for two years and may have
 * bookmarked them, so they land somewhere useful rather than on the error page.
 *
 * Safe to remove after 2026-12.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<readonly [string, string]> = [
  // S1 install flow -> the S-series guide
  ["/s1-setup", `${paths.guides}?product=s-series`],
  ["/s1-setup-legacy", `${paths.guides}?product=s-series`],
  ["/s1-artome-sno", `${paths.guides}?product=s-series`],
  ["/s1-epson-sno", `${paths.guides}?product=s-series`],
  ["/s1-status", `${paths.guides}?product=s-series`],
  ["/s1-tools", `${paths.guides}?product=s-series`],
  ["/s1-hardware", `${paths.guides}?product=s-series`],
  ["/s1-software", `${paths.guides}?product=s-series`],

  // M10 install flow
  ["/m10-setup", `${paths.guides}?product=m10`],
  ["/m10-setup-gen1", `${paths.guides}?product=m10`],
  ["/m10-artome-sno", `${paths.guides}?product=m10`],
  ["/m10-epson-sno", `${paths.guides}?product=m10`],
  ["/m10-hardware", `${paths.guides}?product=m10`],
  ["/m10-software", `${paths.guides}?product=m10`],
  ["/m10-status", `${paths.guides}?product=m10`],

  // X20 install flow
  ["/x20-setup", `${paths.guides}?product=x20`],
  ["/x20-setup-gen1", `${paths.guides}?product=x20`],
  ["/x20-artome-sno", `${paths.guides}?product=x20`],
  ["/x20-epson-sno", `${paths.guides}?product=x20`],
  ["/x20-hardware", `${paths.guides}?product=x20`],
  ["/x20-software", `${paths.guides}?product=x20`],
  ["/x20-lens", `${paths.guides}?product=x20`],
  ["/x20-lens-select", `${paths.guides}?product=x20`],
  ["/x20-status", `${paths.guides}?product=x20`],

  // Registration moved under a nested /register/* shell
  ["/register-artome-serial-no", paths.registerSerial],
  ["/register-projector-serial-no", paths.registerProjector],
  ["/register-owner", paths.registerOwner],
  // Summary goes to the shell so the step guard decides where the user lands.
  ["/register-done", paths.register],

  ["/user-page", paths.account],
  // forgot-password used to navigate here, and it 404'd.
  ["/sign-in", paths.signInUp],
];

export default paths;
